import { writable } from "svelte/store";
import persisted from "./common/persisted.js";

export const profiles = {
	car: {
		id: "car",
		displayName: "Regular Car",
		description: "Prefers fast highways and roadside motels.",
		color: "orangered",
		directions: {
			exclude: ["motorway"]
		},
		wandering: {
			frequency: 0.252,
			amplitude: 100000
		},
		interests: {
			rate: 15,
			queries: ["hotel", "bar", "restaurant", "food", "gas station", "motel", "playground", "service"]
		},
		vehicle: {
			emissions: 0.127,
			speed: 1.1,
			model: [
				// Bottom points
				[-1.2, -0.1, -0.55],
				[1.2, -0.1, -0.55],
				[1.2, -0.1, 0.55],
				[-1.2, -0.1, 0.55],

				// Hood
				[-1.2, -0.1, -0.55],
				[-1.2, -0.1, 0.55],
				[-0.8, 0.1, -0.55],
				[-0.8, 0.1, 0.55],

				// Front grille and headlights suggestion
				[-1.19, 0.0, -0.45],
				[-1.19, 0.0, 0.45],
				[-1.19, 0.1, -0.35],
				[-1.19, 0.1, 0.35],

				// Trunk
				[1.2, -0.1, -0.55],
				[1.2, -0.1, 0.55],
				[0.8, 0.1, -0.55],
				[0.8, 0.1, 0.55],

				// Taillights suggestion
				[1.19, 0.0, -0.45],
				[1.19, 0.0, 0.45],
				[1.19, 0.05, -0.45],
				[1.19, 0.05, 0.45],

				// Cabin points
				[-0.7, 0.5, -0.55],
				[-0.7, 0.5, 0.55],
				[0.7, 0.5, -0.55],
				[0.7, 0.5, 0.55],

				// Roof (flatter than before)
				[-0.6, 0.5, -0.5],
				[-0.6, 0.5, 0.5],
				[0.6, 0.5, -0.5],
				[0.6, 0.5, 0.5],

				// Windshield
				[-0.7, 0.5, -0.55],
				[-0.7, 0.5, 0.55],
				[-0.4, 0.5, -0.5],
				[-0.4, 0.5, 0.5],

				// Rear window
				[0.7, 0.5, -0.55],
				[0.7, 0.5, 0.55],
				[0.4, 0.5, -0.5],
				[0.4, 0.5, 0.5],

				// Door line - front door
				[-0.7, 0.1, -0.551],
				[-0.7, 0.1, 0.551],
				[-0.7, 0.4, -0.551],
				[-0.7, 0.4, 0.551],

				// Door line - rear door
				[0.0, 0.1, -0.551],
				[0.0, 0.1, 0.551],
				[0.0, 0.4, -0.551],
				[0.0, 0.4, 0.551],

				// Door line - trunk
				[0.7, 0.1, -0.551],
				[0.7, 0.1, 0.551],
				[0.7, 0.4, -0.551],
				[0.7, 0.4, 0.551],

				// Side window frames
				[-0.65, 0.4, -0.551],
				[-0.65, 0.4, 0.551],
				[-0.1, 0.4, -0.551],
				[-0.1, 0.4, 0.551],
				[0.65, 0.4, -0.551],
				[0.65, 0.4, 0.551],

				// Beltline (subtle line along the side)
				[-0.8, 0.2, -0.55],
				[-0.8, 0.2, 0.55],
				[0.8, 0.2, -0.55],
				[0.8, 0.2, 0.55]
			],
			wheels: [
				{ position: [-0.7, -0.2, -0.55], radius: 0.25, rotation: [0, 0, 0] },
				{ position: [0.7, -0.2, -0.55], radius: 0.25, rotation: [0, 0, 0] },
				{ position: [-0.7, -0.2, 0.55], radius: 0.25, rotation: [0, 0, 0] },
				{ position: [0.7, -0.2, 0.55], radius: 0.25, rotation: [0, 0, 0] }
			]
		}
	},
	suv: {
		id: "suv",
		displayName: "Sport Utility Vehicle",
		description: "Enjoys dusty country roads and scenic routes.",
		color: "dodgerblue",
		directions: {
			exclude: ["motorway"]
		},
		wandering: {
			frequency: 0.475,
			amplitude: 150000
		},
		interests: {
			rate: 20,
			queries: ["park", "lake", "forest", "restaurant", "pizza", "camping", "offroad", "desert"]
		},
		vehicle: {
			emissions: 0.245,
			speed: 0.985,
			model: [
				// Base points
				[-1.2, -0.3, -0.6],
				[1.2, -0.3, -0.6],
				[1.2, -0.3, 0.6],
				[-1.2, -0.3, 0.6],

				// Front slope (wedge shape)
				[-1.2, -0.3, -0.6],
				[-1.2, -0.3, 0.6],
				[-0.8, 0.2, -0.55],
				[-0.8, 0.2, 0.55],

				// Front upper points
				[-0.8, 0.2, -0.55],
				[-0.8, 0.2, 0.55],

				// Windshield (steep angle)
				[-0.8, 0.2, -0.55],
				[-0.8, 0.2, 0.55],
				[-0.3, 0.6, -0.5],
				[-0.3, 0.6, 0.5],

				// Roof peak
				[-0.3, 0.6, -0.5],
				[-0.3, 0.6, 0.5],

				// Roof back slope
				[-0.3, 0.6, -0.5],
				[-0.3, 0.6, 0.5],
				[1.0, 0.3, -0.5],
				[1.0, 0.3, 0.5],

				// Tailgate slope
				[1.0, 0.3, -0.5],
				[1.0, 0.3, 0.5],
				[1.2, -0.3, -0.6],
				[1.2, -0.3, 0.6],

				// Side panels - left
				[-1.2, -0.3, -0.6],
				[-0.8, 0.2, -0.55],
				[-0.3, 0.6, -0.5],
				[1.0, 0.3, -0.5],
				[1.2, -0.3, -0.6],

				// Side panels - right
				[-1.2, -0.3, 0.6],
				[-0.8, 0.2, 0.55],
				[-0.3, 0.6, 0.5],
				[1.0, 0.3, 0.5],
				[1.2, -0.3, 0.6],

				// Front face (light strip suggestion)
				[-1.18, -0.1, -0.55],
				[-1.18, -0.1, 0.55],
				[-1.18, 0.0, -0.55],
				[-1.18, 0.0, 0.55],

				// Rear face (light strip suggestion)
				[1.18, -0.1, -0.55],
				[1.18, -0.1, 0.55],
				[1.18, 0.0, -0.55],
				[1.18, 0.0, 0.55]
			],
			wheels: [
				{ position: [-0.8, -0.5, -0.61], radius: 0.32, rotation: [0, 0, 0] },
				{ position: [0.8, -0.5, -0.61], radius: 0.32, rotation: [0, 0, 0] },
				{ position: [-0.8, -0.5, 0.61], radius: 0.32, rotation: [0, 0, 0] },
				{ position: [0.8, -0.5, 0.61], radius: 0.32, rotation: [0, 0, 0] }
			]
		}
	},
	van: {
		id: "van",
		displayName: "Camper Van",
		description: "Can't imagine a trip without overnight camping at a national park.",
		color: "coral",
		directions: {
			exclude: ["motorway"]
		},
		wandering: {
			frequency: 0.535,
			amplitude: 300000
		},
		interests: {
			rate: 25,
			queries: ["park", "lake", "forest", "mountain", "waterfall", "scenic", "nature", "rv", "camping"]
		},
		vehicle: {
			emissions: 0.176,
			speed: 0.915,
			model: [
				[-1.2, -0.4, -0.5],
				[1.2, -0.4, -0.5],
				[1.2, -0.4, 0.5],
				[-1.2, -0.4, 0.5],

				// roof
				[-0.8, 0.7, 0.45],
				[-0.8, 0.7, -0.45],
				[0.95, 0.7, 0.5],
				[0.95, 0.7, -0.5],

				// additional planes
				[-1.0, 0.6, 0.5],
				[0.85, 0.6, 0.5],

				[-1.0, 0.6, -0.5],
				[0.85, 0.6, -0.5],

				// hood
				[-1.2, 0.1, -0.5],
				[-1.2, 0.1, 0.5],

				// trunk
				[1.2, 0.1, -0.5],
				[1.2, 0.1, 0.5],

				// sides
				[-1.2, -0.4, -0.5],
				[-1.0, 0.6, -0.5],

				[1.2, -0.4, -0.5],
				[0.85, 0.6, -0.5],

				[-1.2, -0.4, 0.5],
				[-1.0, 0.6, 0.5],

				[1.2, -0.4, 0.5],
				[0.85, 0.6, 0.5]
			],
			wheels: [
				{ position: [-0.8, -0.5, -0.45], radius: 0.28, rotation: [0, 0, 0] },
				{ position: [0.8, -0.5, -0.45], radius: 0.28, rotation: [0, 0, 0] },
				{ position: [-0.8, -0.5, 0.45], radius: 0.28, rotation: [0, 0, 0] },
				{ position: [0.8, -0.5, 0.45], radius: 0.28, rotation: [0, 0, 0] }
			]
		}
	},
	ev: {
		id: "ev",
		displayName: "Electric Vehicle",
		description: "Makes almost no emissions but is in a constant search for chargers.",
		color: "seagreen",
		directions: {
			exclude: ["unpaved", "cash_only_tolls"]
		},
		wandering: {
			frequency: 0.1325,
			amplitude: 100000
		},
		interests: {
			rate: 10,
			queries: ["golf", "club", "casino", "vegan", "mall", "hotel", "restaurant", "coffee", "charger", "charging"]
		},
		vehicle: {
			emissions: 0.0,
			speed: 1.183,
			model: [
				[-1.2, -0.1, -0.675],
				[1.0, -0.1, -0.675],
				[1.0, -0.1, 0.675],
				[-1.2, -0.1, 0.675],

				// roof
				[0.3, 0.8, 0],
				[-0.3, 0.8, 0],

				// additional planes
				[0.5, 0.7, 0],
				[-0.5, 0.7, 0],

				[0.3, 0.6, 0.4],
				[-0.3, 0.6, 0.4],

				[0.3, 0.6, -0.4],
				[-0.3, 0.6, -0.4],

				// hood
				[-1.2, 0.1, -0.5],
				[-1.2, 0.1, 0.5],

				// trunk
				[1.2, 0.1, -0.6],
				[1.2, 0.1, 0.6],

				// sides
				[-1.2, -0.1, -0.675],
				[0.3, 0.6, -0.4],

				[1.0, -0.1, -0.675],
				[0.3, 0.6, -0.4],

				[-1.2, -0.1, 0.675],
				[0.3, 0.6, 0.4],

				[1.0, -0.1, 0.675],
				[0.3, 0.6, 0.4]
			],
			wheels: [
				{ position: [-0.8, -0.2, -0.45], radius: 0.25, rotation: [0, 0, 0] },
				{ position: [0.7, -0.2, -0.45], radius: 0.25, rotation: [0, 0, 0] },
				{ position: [-0.8, -0.2, 0.45], radius: 0.25, rotation: [0, 0, 0] },
				{ position: [0.7, -0.2, 0.45], radius: 0.25, rotation: [0, 0, 0] }
			]
		}
	},
	moto: {
		id: "moto",
		displayName: "Motorcycle",
		description: "Ready to go into the wind wherever the road takes them.",
		color: "black",
		directions: {
			exclude: []
		},
		wandering: {
			frequency: 0.673,
			amplitude: 250000
		},
		interests: {
			rate: 20,
			queries: ["canyon", "river", "lake", "bar", "biker", "grill", "park", "museum", "track"]
		},
		vehicle: {
			emissions: 0.083,
			speed: 1.289,
			model: [
				[-0.4, -0.7, 0],

				// roof
				[-0.8, 0.4, -0.2],
				[-0.8, 0.4, 0.2],
				[-0.6, 0.5, -0.1],
				[-0.6, 0.5, 0.1],

				// trunk
				[1.0, 0.4, 0],
				[1.2, 0.3, -0.5],
				[1.2, 0.3, 0.5],

				// additional planes
				[-0.8, 0.4, -0.2],
				[-0.6, 0.5, -0.1],
				[1.2, 0.3, -0.5],

				[-0.8, 0.4, 0.2],
				[-0.6, 0.5, 0.1],
				[1.2, 0.3, 0.5],

				[-0.6, 0.5, -0.1],
				[-0.4, 0.7, 0],
				[1.2, 0.3, -0.5],

				[-0.6, 0.5, 0.1],
				[-0.4, 0.7, 0],
				[1.2, 0.3, 0.5]
			],
			wheels: [
				{ position: [-0.8, -0.3, 0], radius: 0.35, rotation: [0, 0, 0] },
				{ position: [1.0, -0.3, 0], radius: 0.35, rotation: [0, 0, 0] }
			]
		}
	}
};

export const availableProfiles = [profiles.car, profiles.suv, profiles.van, profiles.ev, profiles.moto];

export const colorPalette = [
	"maroon",
	"firebrick",
	"crimson",
	"coral",

	"red",
	"orangered",
	"goldenrod",
	"orange",

	"navy",
	"blue",
	"dodgerblue",
	"deepskyblue",

	"indigo",
	"blueviolet",
	"orchid",
	"purple",

	"darkslategrey",
	"seagreen",
	"limegreen",
	"turquoise",

	"black",
	"chocolate",
	"darkmagenta",
	"indianred"
];

function createProfileStore() {
	const { subscribe, set, update } = writable();

	return {
		subscribe,
		set,
		repaint(color) {
			update((state) => ({ ...state, color }));
		}
	};
}

const profile = persisted("profile", true, createProfileStore(), undefined, false);

export default profile;
